import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { initialState } from "./initialState";
import { CREATE_GROUP_SELECTOR as CGS } from "../constants";
import { createNewGroup, fetchGroupsByTherapistId, fetchStudentsOfGroup, updateGroup, removeGroup } from "../api";
import { sortByFieldAlphabetically } from "@amplio-common/funcs/commonFunctionalities";
// import { loadAllStudents as loadAllStudentsPromise } from "@app/routes/users/api";
import { loadStudentsInDirect } from "@app/routes/users/api";



const simpleSetPayload = (stateField) => (state, action) => { state[stateField] = action.payload; };
const simpleFieldSelector = (stateField) => CGS(t => t[stateField]);
const sortByName = sortByFieldAlphabetically("name");
// const simpleNestedFieldSelector = (stateField, nestedField) => CGS(t => t[stateField][nestedField]);

export const loadMoreStudentResults = createAsyncThunk(
  "studentGroup/loadMoreStudentResults",
  async (searchQuery, thunkAPI) => {
    const state = thunkAPI.getState();
    // eslint-disable-next-line no-use-before-define
    const page = selectIndirectStudentsPageNumber(state);
    // eslint-disable-next-line no-use-before-define
    const pageSize = 25;
    let params = { page: page + 1, page_size: pageSize, name: searchQuery || undefined };
    const ordering = [{ first_name: "first_name", desc: false }].reduce((all, item, i) => (`${all}${item.desc ? "-" : ""}${item.first_name}${(i + 1) === [{ first_name: "first_name", desc: false }].length ? "" : ","}`), "");
      
    params = { ...params, ordering };
    const { response } = await loadStudentsInDirect(params);

    return { response, searchQuery };
  },
);

export const setSearchQuery = createAsyncThunk(
  "studentGroup/setSearchQuery",
  async (searchQuery, thunkAPI) => {
    thunkAPI.dispatch(loadMoreStudentResults(searchQuery));

    return searchQuery;
  },
);


export const fetchGroupsById = createAsyncThunk(
  "studentGroup/fetchGroupsById",
  async therpistId => {
    const { response } = await fetchGroupsByTherapistId(therpistId);
    return response;
  },
);

export const fetchGroupById = createAsyncThunk(
  "studentGroup/fetchGroupById",
  async (data) => {
    const { therapistId, groupId } = data;
    // const group = thunkAPI.getState().students.groupsDetails[groupId];
    // if (group) return group; // return from cache
    const { response } = await fetchStudentsOfGroup(therapistId, groupId);
    return response;
  },
);

export const createGroup = createAsyncThunk(
  "studentGroup/createGroup",
  async (data, thunkAPI) => {
    const { therapistId, ...rest } = data;
    const { response } = await createNewGroup(therapistId, rest.data);
    thunkAPI.dispatch(fetchGroupById({ therapistId, groupId: response.id }));
    return response;
  },
);

export const editGroup = createAsyncThunk(
  "studentGroup/editGroup",
  async (data, thunkAPI) => {
    const { therapistId, groupId, ...rest } = data;
    const { response } = await updateGroup(therapistId, groupId, rest.data);
    thunkAPI.dispatch(fetchGroupById({ therapistId, groupId: response.id }));
    return response;
  },
);

export const deleteGroup = createAsyncThunk(
  "studentGroup/deleteGroup",
  async data => {
    const { therapistId, groupId } = data;
    removeGroup(therapistId, groupId);
    return groupId;
  },
);

export const studentGroupSlice = createSlice({
  name: "studentGroup",
  initialState,
  reducers: {
    setCategory: (state, action) => { state.currentCategory = action.payload; state.currentGroup = null; },
    setCurrentGroup: (state, action) => { state.currentGroup = state.groups.find(g => g.id === action.payload); },
    setGroup: (state, action) => { state.currentGroup = action.payload; state.currentCategory = null; },
    setIsShowDialog: simpleSetPayload("isShowDialog"),
    setMode: simpleSetPayload("mode"),
    setStudentsToShow: simpleSetPayload("studentsToShow"),
    setGroupsDetails: simpleSetPayload("groupsDetails"),
    setSelectedStudent: simpleSetPayload("currentStudentSelected"),
    setIsStudentRemovalDialogOpen: simpleSetPayload("isStudentRemovalDialogOpen"),
    setIsDialogLoading: simpleSetPayload("isDialogLoading"),
    setIsShowDeleteGroupDialog: simpleSetPayload("isShowDeleteGroupDialog"),
    setIsSearchAll: simpleSetPayload("isSearchAll"),
    setIndirectStudentsPageNumber: simpleSetPayload("indirectStudentsPageNumber"),
    setCurrentIndirectStudents: simpleSetPayload("currentIndirectStudents"),
    setCurrentIndirectHasMore: simpleSetPayload("currentIndirectHasMore"),
  },
  extraReducers: {
    [loadMoreStudentResults.pending]: state => {
      state.isLoadingIndirectUsers = true;
    },
    [loadMoreStudentResults.fulfilled]: (state, action) => {
      const { response, searchQuery } = action.payload;
      if (searchQuery !== state.searchQuery) { // we already fired a new search, drop this one.
        return;
      }
      state.isLoadingIndirectUsers = false;
      state.indirectStudentsPageNumber += 1;
      state.currentIndirectStudents = [...state.currentIndirectStudents, ...response.results];
      state.currentIndirectHasMore = state.currentIndirectStudents.length !== response.count;
    },
    [loadMoreStudentResults.rejected]: state => { state.isLoadingIndirectUsers = false; },
    [fetchGroupsById.pending]: state => {
      state.areGroupsLoading = true;
      state.isGroupFetchErr = false;
    },
    [fetchGroupsById.fulfilled]: (state, action) => {
      state.areGroupsLoading = false;
      state.groups = action.payload;
    },
    [fetchGroupsById.rejected]: state => { state.isGroupFetchErr = true; },
    [createGroup.pending]: state => { state.isCreateGroupErr = false; state.isDialogLoading = true; },
    [createGroup.fulfilled]: (state, action) => {
      state.isDialogLoading = false;
      state.groups.push(action.payload);
      state.currentStudentSelected = null;
    },
    [createGroup.rejected]: state => { state.isCreateGroupErr = true; state.isDialogLoading = false; },
    [editGroup.pending]: state => { state.isEditGroupErr = false; state.isDialogLoading = true; },
    [editGroup.fulfilled]: (state, action) => {
      const { id } = action.payload;
      state.isDialogLoading = false;
      state.groups = state.groups.map(g => g.id === id ? action.payload : g);
      state.currentGroup.students = state.currentGroup.students.filter(s => action.payload.students.includes(s.id));
      state.currentGroup.name = action.payload.name;
      state.isStudentRemovalDialogOpen = null;
    },
    [editGroup.rejected]: (state, action) => { state.dialogErr = action.payload; state.isDialogLoading = false; },
    [fetchGroupById.fulfilled]: (state, action) => {
      const data = action.payload;
      state.currentGroup = data;
      state.currentCategory = null;
    },
    //[fetchGroupById.rejected]: state => { }, TODO: implement error catching strategy
    [deleteGroup.pending]: state => { state.isDialogLoading = true; },
    [deleteGroup.fulfilled]: (state, action) => {
      state.isDialogLoading = false;
      state.groups = state.groups.filter(g => g.id !== action.payload);
    },
    [deleteGroup.rejected]: state => { state.isDialogLoading = false; },
    [setSearchQuery.pending]: state => {
      state.indirectStudentsPageNumber = 0;
      state.currentIndirectStudents = [];
    },
    [setSearchQuery.fulfilled]: (state, action) => {
      state.searchQuery = action.payload;
    },
  },
});

const selectIsCurrentCategory = id => CGS(g => g.currentCategory === id);
const selectIsCurrentGroup = id => CGS(g => g.currentGroup?.id === id);
const selectGroupById = id => CGS(g => g.groups.find(gr => gr.id === id));
const selectGroups = CGS(g => [...g.groups].sort(sortByName));
const selectCurrentGroupStudents = CGS(g => g.currentGroup ? [...g.currentGroup.students].sort(sortByName) : null);

const selectCurrentGroup = simpleFieldSelector("currentGroup");
const selectIsShowDialog = simpleFieldSelector("isShowDialog");
const selectMode = simpleFieldSelector("mode");
const selectStudentsToShow = simpleFieldSelector("studentsToShow");
const selectSelectedStudent = simpleFieldSelector("currentStudentSelected");
const selectSearchQuery = simpleFieldSelector("searchQuery");
const selectIsStudentRemovalDialogOpen = simpleFieldSelector("isStudentRemovalDialogOpen");
const selectIsDialogLoading = simpleFieldSelector("isDialogLoading");
const selectIsShowDeleteGroupDialog = simpleFieldSelector("isShowDeleteGroupDialog");
const selectAreGroupsLoading = simpleFieldSelector("areGroupsLoading");
const selectIsLoadingIndirectUsers = simpleFieldSelector("isLoadingIndirectUsers");
const selectIsSearchAll = simpleFieldSelector("isSearchAll");
const selectIndirectStudentsPageNumber = simpleFieldSelector("indirectStudentsPageNumber");
const selectCurrentIndirectStudents = simpleFieldSelector("currentIndirectStudents");
const selectCurrentIndirectHasMore = simpleFieldSelector("currentIndirectHasMore");

export default studentGroupSlice.reducer;
// action exports
export const { setCategory, setIsShowDialog, setMode, setCurrentGroup,
  setStudentsToShow, setDialogErr, setSelectedStudent, setGroup,
  setIsStudentRemovalDialogOpen, setIsDialogLoading, setIsShowDeleteGroupDialog, setIsSearchAll,
  setIndirectStudentsPageNumber, setCurrentIndirectStudents, setCurrentIndirectHasMore } = studentGroupSlice.actions;
// selector exports
export { selectIsCurrentCategory, selectIsCurrentGroup, selectGroups, selectCurrentGroup,
  selectIsShowDialog, selectMode, selectStudentsToShow,
  selectSelectedStudent, selectSearchQuery, selectGroupById, selectCurrentGroupStudents,
  selectIsStudentRemovalDialogOpen, selectIsDialogLoading, selectIsShowDeleteGroupDialog, selectAreGroupsLoading, selectIsLoadingIndirectUsers,
  selectIsSearchAll, selectIndirectStudentsPageNumber, selectCurrentIndirectStudents, selectCurrentIndirectHasMore,
};
